import React from 'react';
import { Select } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_HUB_LIST, GetHubListResponse } from '../../pages/wms/bags/graphql/queries/getHubList';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../utils/languageUtils';

interface PickupHubSelectFilterProps {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  /** 当选择项变化时回传 Sorting-name（一段码），用于把代码写回查询过滤字段 */
  onSelectSortingName?: (code: string | undefined) => void;
  spanClassName?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Pickup HUB选择过滤器组件
 * 用于Pickup HUB的下拉选择，从HUB列表中选择
 * 获取site_type=1（HUB类型）的所有仓库
 * 返回HUB的ID而不是sorting code
 */
export const PickupHubSelectFilter: React.FC<PickupHubSelectFilterProps> = ({
  label,
  value,
  onChange,
  onSelectSortingName,
  spanClassName,
  placeholder = 'Select HUB',
  className
}) => {
  const { data, loading } = useQuery<GetHubListResponse>(GET_HUB_LIST);

  // 构建下拉选项
  const hubOptions = React.useMemo(() => {
    if (!data?.hubList) return [];

    return data.hubList
      .filter(hub => hub.sortingCode) // 过滤掉没有sortingCode的hub
      .map(hub => {
        // 提取一段码（sorting code的第一段）
        const firstSegment = hub.sortingCode?.split('-')[0] || '';
        return {
          label: `${hub.name} (${firstSegment})`,
          value: hub.id, // 返回HUB的ID
          code: firstSegment, // Sorting-name（第一段码）
          // 用于搜索的文本，包含名称、ID和代码
          searchText: `${hub.name} ${firstSegment} ${hub.sortingCode} ${hub.id}`
        };
      })
      .filter(opt => opt.value); // 过滤空值
  }, [data]);

  // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          showSearch
          allowClear
          placeholder={placeholder}
          value={value}
          onChange={(val, option) => {
            const nextVal = (val as string) || undefined;
            onChange(nextVal);
            if (onSelectSortingName) {
              const code = (option as any)?.code as string | undefined;
              onSelectSortingName(nextVal ? code : undefined);
            }
          }}
          loading={loading}
          options={hubOptions}
          filterOption={(input, option) => {
            const searchText = (option as any)?.searchText || '';
            return searchText.toLowerCase().includes(input.toLowerCase());
          }}
          className="w-full"
          size="middle"
        />
      </div>
    </div>
  );
};

